/**
 * The one modal dialog in the application.
 *
 * V1 section 9 requires modal focus return, and this is the only modal there is,
 * so the requirement has exactly one place to be satisfied. The rules are the
 * platform's own: a native `<dialog>` opened with `showModal()` traps focus,
 * closes on Escape, and marks the rest of the page inert. What the platform does
 * not do after a full re-render is put focus back, because the control that
 * opened the dialog no longer exists, so `App.paint` restores it from the id the
 * opening handler recorded.
 *
 * Why one dialog and not several: every other action in this application is a
 * page, a row action or an inline form. A modal for those would be decoration,
 * and a modal per screen would be nine chances to get focus return wrong.
 */

import type { AppShell } from '../app.js';
import { type RawHtml, html, raw } from '../lib/html.js';

export function renderDialog(app: AppShell): RawHtml {
  const dialog = app.state.dialog;
  if (dialog === null) return raw('');

  if (dialog === 'export') {
    return html`
      <dialog class="sheet-dialog" data-dialog="export" aria-labelledby="export-title">
        <div class="dialog-head">
          <h2 id="export-title">Published release</h2>
          <button class="btn btn-quiet" data-action="dialog:close">Close</button>
        </div>
        <div class="dialog-body">
          <p class="cell-note">
            This is the package the server serves for the current release. Copying it out of this
            application does not update it, and it does not carry a refresh date.
          </p>
          <p class="cell-note">
            Saving the release gives you the published folder and a manifest beside it. Once that
            is on your disk, Skills Manager has no way to reach into it.
          </p>
          <pre class="diff"><code>${app.state.exportBody ?? ''}</code></pre>
        </div>
      </dialog>
    `;
  }

  const toolkit = dialog === 'new-toolkit';
  const titleId = toolkit ? 'new-toolkit-title' : 'new-skill-title';
  const slugId = toolkit ? 'new-toolkit-slug' : 'new-skill-slug';
  const action = toolkit ? 'gate:new-toolkit-submit' : 'gate:new-skill-submit';
  const heading = toolkit ? 'New toolkit' : 'New skill';

  return html`
    <dialog class="sheet-dialog" data-dialog="create" aria-labelledby="create-title">
      <div class="dialog-head">
        <h2 id="create-title">${heading}</h2>
        <button class="btn btn-quiet" data-action="dialog:close">Close</button>
      </div>
      <div class="dialog-body stack-sm">
        <label class="field-label" for="${titleId}">Title</label>
        <input class="field" id="${titleId}" autocomplete="off" />
        <label class="field-label" for="${slugId}">Slug</label>
        <input
          class="field"
          id="${slugId}"
          autocomplete="off"
          spellcheck="false"
          placeholder="lowercase letters, digits and single hyphens"
        />
        <p class="cell-note">
          ${toolkit
            ? 'A toolkit groups skills for discovery. Creating one grants nobody access to the skills inside it.'
            : 'A new skill starts unpublished and private to you. It becomes usable only once an administrator grants it, or you publish a revision after review.'}
        </p>
      </div>
      <div class="dialog-foot">
        <button class="btn btn-secondary" data-action="dialog:close">Cancel</button>
        <button class="btn btn-primary" data-action="${action}">Create</button>
      </div>
    </dialog>
  `;
}